import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { LOGO_URL } from "@/data/brothers";

const links = [
  { to: "/", label: "Home" },
  { to: "/brothers", label: "Our Brothers" },
  { to: "/joseph-house", label: "Joseph House" },
  { to: "/support", label: "Get Involved" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [setScrolled]);

  useEffect(() => setOpen(false), [location.pathname, setOpen]);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-brand-bg/90 backdrop-blur-md border-b border-brand-line shadow-sm" : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-20">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <div className="w-11 h-11 rounded-full bg-brand-surface border border-brand-line overflow-hidden flex items-center justify-center shrink-0">
            <img src={LOGO_URL} alt="The Josephians crest" className="w-full h-full object-contain p-[3px]" />
          </div>
          <div className="leading-tight">
            <div className={`font-serif text-xl transition-colors duration-300 ${scrolled ? "text-brand-ink" : "text-brand-bg"}`}>
              The Josephians
            </div>
            <div className={`text-[10px] uppercase tracking-[0.22em] font-semibold transition-colors duration-300 ${scrolled ? "text-brand-terracotta" : "text-brand-sand"}`}>
              Brothers of Jesus
            </div>
          </div>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          {links.map((l) => {
            const active = location.pathname === l.to;
            const color = active
              ? scrolled ? "text-brand-terracotta" : "text-brand-sand"
              : scrolled ? "text-brand-muted hover:text-brand-terracotta" : "text-brand-bg/85 hover:text-brand-sand";
            return (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`text-sm font-medium tracking-wide transition-colors duration-200 ${color}`}
              >
                {l.label}
              </Link>
            );
          })}
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className={`md:hidden transition-colors duration-300 ${scrolled || open ? "text-brand-ink" : "text-brand-bg"}`}
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </nav>

      {open && (
        <div className="md:hidden bg-brand-bg border-t border-brand-line px-4 py-6 space-y-4" data-testid="mobile-menu">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="block text-brand-ink font-medium py-2"
              data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {l.label}
            </Link>
          ))}
        </div>
      )}
    </header>
  );
}
